import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setAuthHeader } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data.user);
        } catch (e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from OAuth callback, skip /me — AuthCallback will exchange first.
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        fetchMe();
    }, [fetchMe]);

    const loginWithEmail = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        if (data.session_token) setAuthHeader(data.session_token);
        setUser(data.user);
        return data.user;
    };

    const signupWithEmail = async (name, email, password) => {
        const { data } = await api.post('/auth/signup', { name, email, password });
        if (data.session_token) setAuthHeader(data.session_token);
        setUser(data.user);
        return data.user;
    };

    const completeGoogleSession = async (sessionId) => {
        const { data } = await api.post('/auth/google/session', { session_id: sessionId });
        if (data.session_token) setAuthHeader(data.session_token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch (_) { /* ignore */ }
        setAuthHeader(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, loginWithEmail, signupWithEmail, completeGoogleSession, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be inside AuthProvider');
    return ctx;
}
